import {
  Box,
  Button,
  FormHelperText,
  HStack,
  IconButton,
  Input,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"
import { type ReactElement, useEffect, useMemo, useRef, useState } from "react"
import { FiChevronDown, FiSearch, FiX } from "react-icons/fi"

export type SearchableSelectOption = {
  value: string
  label: string
  description?: string
  searchText?: string
}

type SearchableSelectModalProps = {
  title: string
  value: string
  options: SearchableSelectOption[]
  onChange: (nextValue: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  noResultsMessage?: string
  isDisabled?: boolean
  isClearable?: boolean
  triggerWidth?: string
  triggerIcon?: ReactElement
  size?: "sm" | "md" | "lg"
}

const normalizeText = (value: string) =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()

export default function SearchableSelectModal({
  title,
  value,
  options,
  onChange,
  placeholder = "Wybierz…",
  searchPlaceholder = "Szukaj…",
  emptyMessage = "Brak dostępnych opcji",
  noResultsMessage = "Brak wyników",
  isDisabled = false,
  isClearable = false,
  triggerWidth = "100%",
  triggerIcon,
  size = "sm",
}: SearchableSelectModalProps) {
  const picker = useDisclosure()
  const [query, setQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const selectedItemBg = useColorModeValue("blue.50", "blue.900")
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100")

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  )

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeText(query)
    if (!normalizedQuery) return options

    return options.filter((option) => {
      const haystack = normalizeText(
        [option.label, option.description, option.searchText].join(" "),
      )
      return haystack.includes(normalizedQuery)
    })
  }, [options, query])

  useEffect(() => {
    if (!picker.isOpen) setQuery("")
  }, [picker.isOpen])

  return (
    <>
      <HStack align="stretch">
        <Button
          size={size}
          variant="wmsDropdown"
          leftIcon={triggerIcon}
          rightIcon={<FiChevronDown />}
          justifyContent="space-between"
          onClick={picker.onOpen}
          isDisabled={isDisabled}
          w={triggerWidth}
          borderRadius="lg"
        >
          <Box textAlign="left" noOfLines={1}>
            {selectedOption?.label ?? placeholder}
          </Box>
        </Button>
        {isClearable && value ? (
          <IconButton
            size={size}
            aria-label={`Wyczyść ${title.toLowerCase()}`}
            icon={<FiX />}
            variant="outline"
            colorScheme="red"
            onClick={() => onChange("")}
          />
        ) : null}
      </HStack>
      {selectedOption?.description ? (
        <FormHelperText>{selectedOption.description}</FormHelperText>
      ) : null}

      <Modal
        isOpen={picker.isOpen}
        onClose={picker.onClose}
        size="2xl"
        scrollBehavior="inside"
        initialFocusRef={searchInputRef}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3} align="stretch">
              <Input
                size={size}
                ref={searchInputRef}
                placeholder={searchPlaceholder}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <Button
                size={size}
                leftIcon={<FiSearch />}
                onClick={() => searchInputRef.current?.focus()}
              >
                Szukaj
              </Button>
              <Button size={size} onClick={() => setQuery("")}>
                Wyczyść
              </Button>
            </HStack>

            {options.length === 0 ? (
              <Box p={3} borderWidth="1px" borderRadius="md" color="gray.500">
                {emptyMessage}
              </Box>
            ) : filteredOptions.length === 0 ? (
              <Box p={3} borderWidth="1px" borderRadius="md" color="gray.500">
                {noResultsMessage}
              </Box>
            ) : (
              <List
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                maxH="60vh"
                overflowY="auto"
              >
                {filteredOptions.map((option) => {
                  const isSelected = option.value === value

                  return (
                    <ListItem
                      key={option.value}
                      borderBottomWidth="1px"
                      _last={{ borderBottomWidth: 0 }}
                    >
                      <Button
                        variant="ghost"
                        w="100%"
                        h="auto"
                        justifyContent="flex-start"
                        borderRadius={0}
                        py={3}
                        px={4}
                        bg={isSelected ? selectedItemBg : undefined}
                        _hover={{ bg: isSelected ? selectedItemBg : hoverBg }}
                        onClick={() => {
                          onChange(option.value)
                          picker.onClose()
                        }}
                      >
                        <Box textAlign="left" minW={0}>
                          <Text fontWeight="semibold" noOfLines={1}>
                            {option.label}
                          </Text>
                          {option.description ? (
                            <Text fontSize="sm" color="gray.500" noOfLines={2}>
                              {option.description}
                            </Text>
                          ) : null}
                        </Box>
                      </Button>
                    </ListItem>
                  )
                })}
              </List>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size={size} onClick={picker.onClose}>
              Zamknij
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
